/**
 * 앱(iOS · Android 웹뷰) 다크 모드.
 *
 * - 다크는 **앱 웹뷰 안에서, 기기가 다크일 때만** 켠다. PC 웹 · 모바일 브라우저는 계속 라이트.
 * - 컴포넌트 색은 `var(--dk-<토큰>, <원래 색>)` 으로 쓴다. 라이트에서는 `--dk-*` 를 정의하지 않으므로
 *   원래 색이 그대로 쓰이고(라이트 화면 변화 없음), 다크에서만 아래 값으로 바뀐다.
 * - 다크 값은 앱 네이티브 팔레트(iOS `Theme.swift` · Android 테마)와 맞춘다 — 네이티브 화면과 웹 화면이 같은 톤.
 * - 게시글 · 댓글처럼 작성자가 넣은 HTML 은 검정 글자 · 흰 배경만 보정하고(`appDarkRichContentCss`), 결재 본문은 통째로 반전한다(`appDarkInvertedPaperCss`).
 */

export const APP_DARK_ATTRIBUTE = 'data-color-mode';

export const APP_DARK_TOKENS = {
  // 면
  bg: '#111214',
  'bg-subtle': '#1C1D21',
  'bg-muted': '#26282D',
  'bg-strong': '#2F3136',
  'accent-soft': '#172B44',
  'accent-tint': '#1E3656',
  'danger-soft': '#3D1E23',
  'purple-soft': '#33203A',
  'purple-text': '#DCA6D9', // 보라 바탕(purple-soft) 위 보라 글자 — 결재 AI 어시스턴트 헤더 · 제안 문구
  'warning-soft': '#3A2E14',
  // 글자 · 아이콘
  text: '#F2F3F5',
  'text-primary': '#E3E5E8',
  'text-strong': '#C5C9D0',
  'text-secondary': '#9197A1',
  'text-dim': '#62676F',
  'text-disabled': '#4A4E55',
  accent: '#4A94FF',
  danger: '#FF6B78',
  // 선
  line: '#2F3136',
  'line-strong': '#3A3D43',
  'accent-line': '#1E3656',
  'danger-line': '#5A2A31',
  'purple-line': '#4A2D52',
  // 검정 버튼 → 다크에서는 밝은 버튼 (앱 네이티브 inverseBackground · inverseText)
  'inverse-bg': '#F2F3F5',
  'inverse-bg-hover': '#D9DCE1',
  'inverse-bg-pressed': '#C5C9D0',
  'inverse-text': '#000000',
  // 투명으로 사라지는 그라디언트 시작점 (bg · bg-subtle 의 알파 0)
  'bg-clear': 'rgba(17, 18, 20, 0)',
  'bg-subtle-clear': 'rgba(28, 29, 33, 0)',
} as const;

/** `html[data-color-mode='dark']` 에만 `--dk-*` 를 정의하는 CSS (GlobalStyles) */
export const appDarkTokensCss = `html[${APP_DARK_ATTRIBUTE}='dark'] {
${Object.entries(APP_DARK_TOKENS)
  .map(([name, value]) => `  --dk-${name}: ${value};`)
  .join('\n')}
}`;

/**
 * SVG 아이콘(public/icons · 인라인 svg)에 박힌 검정 · 회색 선 / 채우기를 다크 글자 색으로.
 * CSS 속성이 SVG 의 fill · stroke 속성(presentation attribute)보다 우선한다.
 */
const SVG_ICON_COLORS: Record<string, string[]> = {
  text: [
    '#000',
    '#000000',
    'black',
    '#1A1A1A',
    '#131313',
    '#1D2025',
    '#111',
    '#222',
  ],
  'text-primary': ['#34383D', '#333', '#3F4254'],
  'text-strong': ['#4D5157', '#555962', '#5F636B', '#555', '#666'],
  'text-secondary': ['#888D96', '#888', '#999'],
  'text-dim': ['#B7BBC2', '#BBB'],
  'text-disabled': ['#D1D8E2', '#D4D7DB'],
};
const svgIconCss = Object.entries(SVG_ICON_COLORS)
  .map(([token, colors]) =>
    (['fill', 'stroke'] as const)
      .map((attr) => {
        const match = colors.map((c) => `[${attr}="${c}" i]`).join(', ');
        return `html[${APP_DARK_ATTRIBUTE}='dark'] svg:is(${match}), html[${APP_DARK_ATTRIBUTE}='dark'] svg :is(${match}) { ${attr}: var(--dk-${token}); }`;
      })
      .join('\n'),
  )
  .join('\n');

/** `html[data-color-mode='dark']` 에만 `--dk-*` · 아이콘 색을 정의하는 CSS (GlobalStyles) */
export const appDarkGlobalCss = `${appDarkTokensCss}
${svgIconCss}
/* F&F 로고(색 지정 없는 SVG = 검정)는 다크에서 흰 글자색 */
html[${APP_DARK_ATTRIBUTE}='dark'] svg.fnf-logo {
  fill: var(--dk-text);
}
/* 색 지정이 아예 없는 SVG 아이콘(기본 fill = 검정, 예: 댓글 입력칸 @)도 글자색으로.
   클래스로 색을 주는 아이콘(MUI SvgIcon 등)과 fill 속성이 있는 아이콘은 건드리지 않는다 */
html[${APP_DARK_ATTRIBUTE}='dark'] svg:not([fill]):not([class]) :is(path, circle, rect, ellipse, polygon):not([fill]) {
  fill: var(--dk-text);
}
/* <img src="/icons/*.svg"> 아이콘은 CSS 로 색을 못 바꾼다 — 밝기만 뒤집고 색상은 유지 (로고 제외) */
html[${APP_DARK_ATTRIBUTE}='dark'] img[src^="/icons/"][src$=".svg"]:not([src^="/icons/logo/"]) {
  filter: invert(1) hue-rotate(180deg);
}`;

/**
 * 앱이 다크여도 늘 라이트로 그리는 화면 — 세일즈(/m/sales · /m/sales2 · /m/newsales).
 * 세일즈 컴포넌트는 다크 토큰을 하나도 안 써서, 바탕만 어두워지고 글자 · 카드는 라이트로 남아 반만 다크로 보였다 (2026-10-13).
 * 다크 토큰으로 전면 대응할 때까지 라이트로 고정. styles/InlineScript.tsx 도 같은 접두사를 본다 (하이드레이션 전).
 */
export const APP_LIGHT_ONLY_PATH_PREFIXES = [
  '/m/sales',
  '/m/newsales',
] as const;

export const isAppLightOnlyPath = (pathname: string) =>
  APP_LIGHT_ONLY_PATH_PREFIXES.some((prefix) => pathname.startsWith(prefix));

/**
 * 앱 웹뷰에서 다크로 그릴지 — 앱이 다크 모드를 허용한다고 알려 준 경우(`window.portalAppColorScheme`)에만.
 * 예전 앱(표시 없음)은 브리지가 있어도 늘 라이트 — 예전 앱 화면(흰 헤더 · 바탕) 안에서 웹만 다크가 되지 않게.
 * 라이트 고정 화면(`isAppLightOnlyPath`)은 앱이 다크여도 라이트.
 */
export const shouldUseAppDarkMode = (
  prefersDark: boolean,
  pathname: string = typeof window === 'undefined'
    ? ''
    : window.location.pathname,
) => {
  if (typeof window === 'undefined' || !window.ReactNativeWebView) return false;
  if (isAppLightOnlyPath(pathname)) return false;
  const scheme = window.portalAppColorScheme;
  if (scheme === 'dark') return true;
  if (scheme === 'system') return prefersDark;
  return false;
};

/** `<html>` 에 모드를 표시한다 — `color-scheme` 으로 스크롤바 · 기본 입력창도 같은 모드 */
export const applyAppColorMode = (mode: 'light' | 'dark') => {
  const root = document.documentElement;
  root.setAttribute(APP_DARK_ATTRIBUTE, mode);
  root.style.colorScheme = mode;
};

const DARK = `html[${APP_DARK_ATTRIBUTE}='dark']`;

/** 작성자가 넣은 인라인 색 — 검정 글자 · 흰 배경 (대소문자 무시) */
const INLINE_BLACK_TEXT = [
  'color: rgb(0, 0, 0)',
  'color:rgb(0,0,0)',
  'color: #000',
  'color:#000',
  'color: black',
  'color:black',
];
const INLINE_WHITE_BACKGROUND = [
  'background-color: rgb(255, 255, 255)',
  'background-color:rgb(255,255,255)',
  'background-color: #fff',
  'background-color:#fff',
  'background-color: white',
  'background: rgb(255, 255, 255)',
  'background: #fff',
  'background: white',
];
const styleContains = (list: string[]) =>
  list.map((value) => `[style*="${value}" i]`).join(', ');

/**
 * 작성자 HTML(게시글 본문 · 댓글 · 결재 의견)을 다크에서 읽히게 — 기본 글자는 부모 색(밝은 글자)을 따르고,
 * 작성자가 직접 넣은 검정 글자와 흰 배경만 다크 색으로 보정한다. 그 밖의 작성자 색(빨강 · 파랑 등)은 그대로.
 * styled 템플릿 안에 `${appDarkRichContentCss}` 로 넣는다.
 */
export const appDarkRichContentCss = `
  ${DARK} & :is(${styleContains(INLINE_BLACK_TEXT)}),
  ${DARK} & font:is([color="#000000" i], [color="#000" i], [color="black" i]) {
    color: var(--dk-text) !important;
  }
  ${DARK} & :is(${styleContains(INLINE_WHITE_BACKGROUND)}) {
    background-color: transparent !important;
  }
  ${DARK} & :is(table, th, td) {
    border-color: var(--dk-line-strong);
  }
`;

/** MUI `sx` 용 — `appDarkRichContentCss` 와 같은 규칙 */
export const appDarkRichContentSx = {
  [`${DARK} & :is(${styleContains(
    INLINE_BLACK_TEXT,
  )}), ${DARK} & font:is([color="#000000" i], [color="#000" i], [color="black" i])`]:
    { color: 'var(--dk-text) !important' },
  [`${DARK} & :is(${styleContains(INLINE_WHITE_BACKGROUND)})`]: {
    backgroundColor: 'transparent !important',
  },
  [`${DARK} & :is(table, th, td)`]: { borderColor: 'var(--dk-line-strong)' },
} as const;

/**
 * 결재 문서 본문처럼 작성자 HTML 에 색이 많이 박힌 영역을 다크로 — 본문을 라이트(원래 색)로 그린 뒤 통째로 반전한다.
 * 양식 템플릿에 표 머리줄 연회색만 20가지가 넘게 박혀 있어(#ebebeb · #d9d9d9 · rgb(241, 241, 241) …)
 * `appDarkRichContentCss` 처럼 색을 하나씩 바꾸면 빠지는 색이 생긴다. 반전하면 흰 바탕은 어둡게, 검정 글자는 밝게,
 * 연회색 칸은 짙은 회색으로 바뀌고, 파랑 · 빨강 같은 색은 hue-rotate 로 색상을 유지한 채 밝기만 뒤집힌다.
 * - 영역 안에서는 `--dk-*` 를 무효(initial)로 되돌려 안쪽 컴포넌트도 라이트 색으로 그린 뒤 반전되게 한다.
 * - 바탕은 칠하지 않는다(반전한 흰색 = 검정이 화면 바탕과 어긋나지 않게) — 작성자가 넣은 흰 배경도 투명으로.
 * - 표 선은 반전 뒤 다크 선 색(`--dk-line-strong` 근처)이 되도록 밝은 회색으로.
 * - 이미지 · 영상은 한 번 더 반전해 원래 색으로.
 */
export const appDarkInvertedPaperCss = `
  ${DARK} & {
    color: #000000;
    color-scheme: light;
    filter: invert(1) hue-rotate(180deg);
${Object.keys(APP_DARK_TOKENS)
  .map((name) => `    --dk-${name}: initial;`)
  .join('\n')}
  }
  ${DARK} & :is(${styleContains(INLINE_WHITE_BACKGROUND)}) {
    background-color: transparent !important;
  }
  ${DARK} & :is(table, th, td) {
    border-color: #C2C2C2 !important;
  }
  ${DARK} & :is(img, video, picture, canvas) {
    filter: invert(1) hue-rotate(180deg);
  }
`;
